// The message body, rendered inline in a shadow root. The shadow root keeps the app's CSS and the
// mail's apart; it is no security wall, so everything the mail could do goes through here first:
// DOMPurify strips scripts, handlers, forms and the like, and the browser's own CSS parser reads the
// mail's styles so that nothing loads from another host until the reader asks for remote images.

import DOMPurify from 'dompurify'

// Defaults for mail that sets no styles of its own; they come first, so the mail's CSS wins. The
// mail's `html`, `body` and `:root` selectors are moved to `.mc-html` and `.mc-body`. Mail is
// written for white paper, so the dark theme keeps it white too.
const BASE_CSS = ':host{all:initial;display:block}.mc-html{overflow-x:auto;background:#fff}.mc-body{margin:12px 16px 16px;color:#1d1c21;font:15px/1.46 "Inter",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",sans-serif;overflow-wrap:anywhere}'

// Every network URL an image-like attribute or a CSS `url()` names; srcset lists are split below.
const LOADS = /\b(?:src|background|poster)\s*=\s*["']?\s*(https?:[^"'\s>]*)|url\(\s*["']?\s*(https?:[^"')\s]*)|\bsrcset\s*=\s*["']([^"']*)/gi

// What the reader shows: no forms, media players, dialogs, popovers, templates or slots.
const PURIFY = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style', 'form', 'input', 'button', 'select', 'option', 'optgroup', 'textarea', 'datalist', 'output', 'video', 'audio', 'track', 'dialog', 'template', 'slot'],
  FORBID_ATTR: ['action', 'method', 'popover', 'popovertarget', 'popovertargetaction', 'command', 'commandfor', 'autofocus', 'slot', 'part', 'exportparts', 'nonce'],
  RETURN_DOM: true,
}

// Attributes that load what they name; `href` loads only on `link`, which never gets through.
const URL_ATTRS = ['src', 'srcset', 'background', 'poster']

// CSS rules the reader keeps; `@import`, `@property`, `@counter-style`, `@page` and the rest go.
const RULES = new Set(['CSSStyleRule', 'CSSMediaRule', 'CSSSupportsRule', 'CSSContainerRule', 'CSSLayerBlockRule', 'CSSLayerStatementRule', 'CSSKeyframesRule', 'CSSKeyframeRule', 'CSSFontFaceRule', 'CSSNestedDeclarations'])

// The browser serializes every URL as `url("...")`. A value it keeps as written (one with `var()`),
// an escape or a bare `url(` is not read, so it goes.
const UNREAD = /\\|\b(?:var|env|attr)\(|url\((?!")/i
const CSS_STRING = /"((?:[^"\\]|\\.)*)"/g
const CSS_URL = /url\("((?:[^"\\]|\\.)*)"\)/gi

// The mail's own document selectors, and selectors that reach the host or out of the shadow root.
const ROOTS = /(^|[\s,>+~(])(html|body|:root)(?![\w-])/gi
const ESCAPES = /:host|::slotted|::part/i

/** This page's origin, which serves the `/f/` image links; empty outside a browser. */
export function ownOrigin() {
  return globalThis.location?.origin || ''
}

/** True when `url` would load from a host other than `origin`; relative URLs stay on it. */
export function isRemote(url, origin = ownOrigin()) {
  let parsed
  try {
    parsed = new URL(url, origin || undefined)
  } catch {
    return false
  }
  return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.origin !== origin
}

/** True when the HTML would load an image or other resource from a host other than `origin`. */
export function hasRemoteImages(html, origin = ownOrigin()) {
  for (const match of (html || '').matchAll(LOADS)) {
    const urls = match[3] === undefined ? [match[1] || match[2]] : match[3].match(/https?:[^\s,]*/gi) || []
    if (urls.some((url) => isRemote(url, origin))) return true
  }
  return false
}

/** `<id@host>` and `id@host` name the same part. */
export function bareContentId(value) {
  return (value || '').trim().replace(/^<|>$/g, '')
}

/** `.mc-html`/`.mc-body` for the `html`, `body` and `:root` of a mail selector. */
export function scopeSelector(selector) {
  return selector.replace(ROOTS, (whole, lead, name) => `${lead}${name.toLowerCase() === 'body' ? '.mc-body' : '.mc-html'}`)
}

/** The text of a serialized CSS string. */
function cssText(raw) {
  return raw.replace(/\\([0-9a-f]{1,6} ?|[\s\S])/gi, (whole, code) => (/^[0-9a-f]/i.test(code) && code.trim().length ? String.fromCodePoint(parseInt(code, 16)) : code))
}

const cssString = (value) => `"${value.replace(/["\\]/g, '\\$&')}"`

/** The `/f/` link of a `cid:` URL's part, else null. */
function contentLink(url, links) {
  const match = /^\s*cid:(\S+)/i.exec(url)
  if (!match) return null
  try {
    return links[decodeURIComponent(match[1])] || null
  } catch {
    return null
  }
}

/**
 * One CSS value as the reader keeps it: `cid:` URLs become their `/f/` links, and `null` when it
 * cannot be read or names another host while `remote` is off.
 */
export function cssValue(value, { remote = false, links = {}, origin = ownOrigin() } = {}) {
  if (!value || UNREAD.test(value)) return null
  const swapped = value.replace(CSS_URL, (whole, raw) => {
    const link = contentLink(cssText(raw), links)
    return link ? `url(${cssString(link)})` : whole
  })
  if (remote) return swapped
  for (const match of swapped.matchAll(CSS_STRING)) if (isRemote(cssText(match[1]), origin)) return null
  return swapped
}

let purify = null
// The options of the mail being sanitized, for the attribute hook.
let hookOptions = null

/** Keeps the declarations `cssValue` accepts and drops custom properties; true when one remains. */
function cleanStyle(style, options) {
  const kept = []
  for (const name of Array.from(style)) {
    const value = name.startsWith('--') ? null : cssValue(style.getPropertyValue(name), options)
    if (value !== null) kept.push([name, value, style.getPropertyPriority(name)])
  }
  style.cssText = ''
  for (const [name, value, priority] of kept) style.setProperty(name, value, priority)
  return kept.length > 0
}

function cleanRules(owner, options) {
  const rules = owner.cssRules
  for (let index = rules.length - 1; index >= 0; index--) {
    const rule = rules[index]
    const kind = rule.constructor.name
    // Fonts load only from data: URLs, images or not.
    const drop = !RULES.has(kind)
      || (kind === 'CSSFontFaceRule' && cssValue(rule.cssText.replace(/^[^{]*\{|\}\s*$/g, ''), { ...options, remote: false }) === null)
      || (kind === 'CSSStyleRule' && ESCAPES.test(rule.selectorText))
    if (drop) {
      owner.deleteRule(index)
      continue
    }
    if (kind === 'CSSStyleRule') rule.selectorText = scopeSelector(rule.selectorText)
    if (rule.style && kind !== 'CSSFontFaceRule') cleanStyle(rule.style, options)
    if (rule.cssRules) cleanRules(rule, options)
  }
}

function mailSheet(css, options) {
  const sheet = new CSSStyleSheet()
  // replaceSync never loads `@import`; the rules it does read are cleaned in place.
  sheet.replaceSync(css)
  cleanRules(sheet, options)
  return sheet
}

function cleanAttributes(node) {
  const options = hookOptions
  for (const name of URL_ATTRS) {
    const value = node.getAttribute(name)
    if (value === null) continue
    const link = contentLink(value, options.links)
    if (link) node.setAttribute(name, link)
    const urls = name === 'srcset' ? value.split(',').map((item) => item.trim().split(/\s+/)[0]) : [link || value]
    if (!options.remote && urls.some((url) => isRemote(url, options.origin))) node.removeAttribute(name)
  }
  if (node.hasAttribute('style') && !cleanStyle(node.style, options)) node.removeAttribute('style')
  if (node.nodeName === 'IMG') node.setAttribute('referrerpolicy', 'no-referrer')
  if ((node.nodeName === 'A' || node.nodeName === 'AREA') && node.hasAttribute('href') && !node.getAttribute('href').startsWith('#')) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
}

function sanitizer() {
  if (!purify) {
    purify = DOMPurify(window)
    purify.addHook('afterSanitizeAttributes', cleanAttributes)
  }
  return purify
}

let baseSheet = null

/**
 * The mail as the reader shows it: `{ sheet, root }`, the cleaned style sheet and `.mc-html`
 * holding `.mc-body` with the mail's body. Images on `origin` always load, other hosts only with
 * `remote`. `images` (`{ contentId: url }`) replaces the `cid:` references of a received HTML part
 * with the `/f/` links of its parts.
 */
export function mailBody(html, { remote = false, images = {}, origin = ownOrigin() } = {}) {
  const links = {}
  for (const [id, url] of Object.entries(images || {})) links[bareContentId(id)] = url
  const options = { remote, links, origin }
  // An inert document: nothing in it loads or runs.
  const doc = new DOMParser().parseFromString(html || '', 'text/html')
  const css = Array.from(doc.querySelectorAll('style'), (style) => {
    style.remove()
    return style.textContent
  }).join('\n')
  hookOptions = options
  let body
  try {
    body = sanitizer().sanitize(doc.body, PURIFY)
  } finally {
    hookOptions = null
  }
  const root = document.createElement('div')
  root.className = 'mc-html'
  const main = document.createElement('div')
  for (const { name, value } of Array.from(body.attributes)) {
    if (name === 'bgcolor') root.style.backgroundColor = value
    else if (name === 'background') root.style.backgroundImage = `url(${cssString(value)})`
    else if (name !== 'class') main.setAttribute(name, value)
  }
  main.className = `mc-body ${body.getAttribute('class') || ''}`.trim()
  // The body's background paints the whole canvas, as in a mail client.
  for (const name of Array.from(main.style)) {
    if (!name.startsWith('background')) continue
    root.style.setProperty(name, main.style.getPropertyValue(name), main.style.getPropertyPriority(name))
    main.style.removeProperty(name)
  }
  if (!main.style.length) main.removeAttribute('style')
  main.append(...body.childNodes)
  root.append(main)
  return { sheet: mailSheet(css, options), root }
}

const shown = new WeakMap()

/** Renders the mail into `host`'s shadow root; a host already showing the same mail is left alone. */
export function renderMail(host, html, options = {}) {
  const key = JSON.stringify([html, Boolean(options.remote), options.images || {}])
  if (shown.get(host) === key) return
  shown.set(host, key)
  let shadow = host.shadowRoot
  if (!shadow) {
    shadow = host.attachShadow({ mode: 'open' })
    // `#name` links scroll to their anchor in the mail; the app's own hash routes stay untouched.
    shadow.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[href^="#"], area[href^="#"]')
      if (!link) return
      event.preventDefault()
      const id = decodeURIComponent(link.getAttribute('href').slice(1))
      if (id) (shadow.getElementById(id) || shadow.querySelector(`a[name="${CSS.escape(id)}"]`))?.scrollIntoView({ block: 'start' })
    })
  }
  if (!baseSheet) {
    baseSheet = new CSSStyleSheet()
    baseSheet.replaceSync(BASE_CSS)
  }
  const { sheet, root } = mailBody(html, options)
  shadow.adoptedStyleSheets = [baseSheet, sheet]
  shadow.replaceChildren(root)
}

/** `mailto:list@x.test?subject=stop&body=...` -> `{ to, subject, body }`. */
export function parseMailto(value) {
  const raw = (value || '').replace(/^mailto:/i, '')
  const [address, query = ''] = raw.split('?')
  const params = new URLSearchParams(query)
  return { to: decodeURIComponent(address), subject: params.get('subject') || '', body: params.get('body') || '' }
}
